import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import { ReactElement, useEffect, useMemo } from 'react';
import {
  GQL_MUTATION_CREATE_INTEGRATION,
  GQL_QUERY_INDEX_INTEGRATIONS,
} from '../utility/queries';
import { IntegrationState, IntegrationType } from '@sc/graphql';
import { Button, Loader } from '@sc/shared-ui';
import {
  decodeOAuthState,
  integrationOptions,
  sourceHashForIntegrationType,
} from '../utility/integrations';
import { useRouter, useSearch } from '@tanstack/react-router';
import { notify } from '../utility/notify';
import { useAlerts } from '../providers/alerts';
import { track } from '../utility/analytics';

export const Integrations = (): ReactElement => {
  const { data } = useSuspenseQuery(GQL_QUERY_INDEX_INTEGRATIONS, {
    variables: {
      input: undefined,
    },
  });

  const router = useRouter();

  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const search = useSearch({
    from: '/_authenticated/integrations',
  });

  const decodedState = useMemo(
    () => decodeOAuthState(search?.state),
    [search?.state]
  );

  const [createIntegration] = useMutation(GQL_MUTATION_CREATE_INTEGRATION, {
    onCompleted: () => {
      notify.success('Successfully connected.');

      if (decodedState) {
        track('Integration Connected', {
          integrationType: decodedState.type,
          origin: decodedState.projectUuid
            ? 'project-sources'
            : 'integrations-page',
        });
      }
    },
    onError,
    refetchQueries: [GQL_QUERY_INDEX_INTEGRATIONS],
  });

  const options = useMemo(
    () =>
      Object.values(integrationOptions).map((opt) => ({
        ...opt,
        oAuthUrl: opt.oAuthUrl(),
        pending: search?.code && decodedState?.type === opt.type,
        state:
          data.indexIntegrations.items.find(
            ({ type }) => type === IntegrationType[opt.type]
          )?.state ?? opt.state,
      })),
    [data.indexIntegrations.items, search?.code, decodedState]
  );

  useEffect(() => {
    if (search?.code && decodedState) {
      void createIntegration({
        variables: {
          input: {
            integrationType: decodedState.type,
            authorisationCode: search.code,
          },
        },
      }).finally(() => {
        if (decodedState.projectUuid) {
          void router.navigate({
            to: '/kb/$uuid',
            params: { uuid: decodedState.projectUuid },
            hash: sourceHashForIntegrationType[decodedState.type],
          });
        } else {
          void router.navigate({
            to: '/integrations',
            search: { code: undefined, state: undefined },
          });
        }
      });
    }
  }, [createIntegration, router, search?.code, decodedState]);

  return (
    <>
      <div className="p-5 space-y-5">
        <h1 className="text-h1-small font-semibold font-grotesque">
          Integrations
        </h1>
        <p className="text-text-low-priority text-sm">
          Integrations allow you to retrieve and update data automatically for
          your knowledge bases.
        </p>
      </div>
      <div className="flex gap-5 flex-wrap px-5 mt-5">
        {options.map((opt) => (
          <div className="border border-grey-t-800 rounded-xs bg-background-tertiary max-w-[360px] grow flex flex-col">
            <div className="p-5 flex space-x-5 border-b border-grey-t-800">
              <div className="border border-grey-t-800 p-3.5 h-fit flex items-center justify-center rounded-xs">
                <opt.logo className="size-6" />
              </div>
              <div className="space-y-2">
                <h4 className="text-h4 font-grotesque font-bold">{opt.name}</h4>
                <p className="text-sm text-text-low-priority">
                  {opt.description}
                </p>
              </div>
            </div>
            <div className="p-5 flex space-x-2 items-center grow">
              {opt.pending ? (
                <Loader className="size-5" />
              ) : opt.state === IntegrationState.new ||
                opt.state === IntegrationState.declined ? (
                <Button
                  button={{
                    text: 'Connect',
                    type: 'primary',
                    size: 'small',
                    shrink: true,
                    onClick: () =>
                      track('Integration Connect Clicked', {
                        integrationType: opt.type,
                        origin: 'integrations-page',
                      }),
                  }}
                  link={{
                    href: opt.oAuthUrl,
                  }}
                />
              ) : (
                <>
                  <Button
                    button={{
                      text: 'Details',
                      type: 'low-priority',
                      size: 'small',
                      shrink: true,
                    }}
                    link={{
                      href: opt.oAuthUrl,
                    }}
                  />
                  <Button
                    button={{
                      text: 'Remove',
                      type: 'delete',
                      size: 'small',
                      shrink: true,
                    }}
                  />
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
